import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext'; // 가정: 인증 및 API 요청을 처리하는 AuthContext
import WaitComponent from './league/WaitComponent';
import DraftComponent from './league/DraftComponent';
import LineupComponent from './league/LineupComponent';
import ResultComponent from './league/ResultComponent';
import { LeagueDetailDto } from '@playball/common/dto/League';
import { DataResponse } from '@playball/common/dto/Common';

// --- 메인 페이지 컴포넌트 ---
const LeagueSessionPage: React.FC = () => {
    const { leagueCode } = useParams<{ leagueCode: string }>();
    const { user, fetchApi, isLoading: isAuthLoading } = useAuth();

    const [leagueData, setLeagueData] = useState<LeagueDetailDto | null>(null);
    const [isLoading, setIsLoading] = useState(true);;
    const [error, setError] = useState<string | null>(null);

    const fetchLeagueData = useCallback(async () => {
        if (!leagueCode) {
            setError('리그 코드가 유효하지 않습니다.');
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const data: DataResponse<LeagueDetailDto> = await fetchApi(`/api/league/session/${leagueCode}`);
            setLeagueData(data.data);
        } catch (err: any) {
            setError(err.message || '리그 정보를 불러오는 데 실패했습니다.');
        } finally {
            setIsLoading(false);
        }
    }, [leagueCode, fetchApi]);

    // 컴포넌트가 처음 마운트되거나 leagueCode가 변경될 때 리그 정보를 가져옵니다.
    useEffect(() => {
        fetchLeagueData();
    }, [fetchLeagueData]);

    useEffect(() => {

        if (!leagueData) return;

        if (leagueData.status === 'scheduled' || leagueData.status === 'ready'){
            const draftAt = new Date(leagueData.draft_at);
            let timeoutId: NodeJS.Timeout | null = null;
            if (draftAt.getTime() <= Date.now()) {
                timeoutId = setTimeout(fetchLeagueData, 1000);
            } else {
                // 최대 30초마다 다시 체크
                timeoutId = setTimeout(fetchLeagueData, Math.min(draftAt.getTime() - Date.now(), 30000));
            }

            // 컴포넌트가 언마운트될 때 인터벌을 정리(cleanup)합니다.
            return () => {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
            };
        }
        
    }, [leagueData, fetchLeagueData]); // fetchLeagueData 함수가 변경될 때마다 인터벌을 재설정합니다.
    
    // 4. 로딩 및 에러 상태에 따른 UI 처리
    
    if (isLoading || isAuthLoading) { 
        return <div className="container mt-5 text-center"><h2> 리그 정보 로딩 중...</h2></div>;
    }

    if (error) {
        // 에러 발생 시, 에러 메시지를 보여주고 홈페이지로 리다이렉트할 수 있습니다.
        alert(error);
        return <Navigate to="/dashboard" replace />;
    }

    if (!user?.id && !isAuthLoading){
        alert("no user")
        return <Navigate to="/login" replace />;
    }

    if (!leagueData) {
        // 리그 데이터가 없는 경우
        return <Navigate to="/dashboard" replace />;
    }

    // 5. 리그 상태(status)에 따라 적절한 컴포넌트를 렌더링합니다.
    const renderContentByStatus = () => {
        switch (leagueData.status) {
            case 'scheduled':
                return <WaitComponent league={leagueData} />;
            case 'ready':
            case 'draft':
                return <DraftComponent league={leagueData} />;
            case 'inprogress':
                // 'inprogress' (진행 중) 상태일 때 라인업 컴포넌트 표시
                return <LineupComponent league={leagueData} />;
            case 'finished':
                // 'finished' (종료) 상태일 때 결과 컴포넌트 표시
                return <ResultComponent league={leagueData} />;
            case 'canceled':
                // 'canceled' (취소) 상태일 때
                return <div><h2>리그가 취소되었습니다.</h2></div>;
            default:
                // 알 수 없는 상태일 때
                return <div><h2>유효하지 않은 리그 상태입니다.</h2></div>;
        }
    };

    return (
        <div className="container">
            {/* 여기에 모든 세션 페이지에서 공통으로 사용할 헤더나 레이아웃을 둘 수 있습니다. */}
            {renderContentByStatus()}
        </div>
    );
};

export default LeagueSessionPage;